import { Component, Input, OnInit, inject, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MemberDashboardService } from '../member-dashboard.service';

type DaySummary = { entries: number; completed: number };

type HabitSlotCard = {
  habitIcon: string | null;
  habitName: string | null;
  title: string | null;
  description: string | null;
  habitJourneyDescription: string | null;
  status: string | null;
  time: string | null; // 24h from API
};

@Component({
  selector: 'app-habits',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatDialogModule,
  ],
  templateUrl: './habits.component.html',
  styleUrl: './habits.component.css',
})
export class HabitsComponent implements OnInit {
  @Input() memberId: any;
  readonly dialog = inject(MatDialog);

  selectedMonth: number = new Date().getMonth(); // 0-based for UI
  selectedYear: number = new Date().getFullYear();

  calendarDays: Array<{
    day: number | null;
    date: Date | null;
    dateKey?: string;
    isCurrentMonth: boolean;
    entries?: number;
    completed?: number;
  }> = [];

  months: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  years: number[] = [];

  isLoading = false;
  monthViewResponse: any = null;
  private monthViewItems: any[] = [];
  private dayMap: Record<string, DaySummary> = {};

  constructor(private dashboard: MemberDashboardService) {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      this.years.push(i);
    }
  }

  ngOnInit(): void {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    if (this.selectedYear > currentYear) this.selectedYear = currentYear;
    if (this.selectedYear === currentYear && this.selectedMonth > currentMonth) {
      this.selectedMonth = currentMonth;
    }

    this.loadMonthView();
  }

  onMonthYearChange(): void {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    if (this.selectedYear > currentYear) this.selectedYear = currentYear;
    if (this.selectedYear === currentYear && this.selectedMonth > currentMonth) {
      this.selectedMonth = currentMonth;
    }

    this.loadMonthView();
  }

  isMonthDisabled(monthIndex: number): boolean {
    const now = new Date();
    if (this.selectedYear === now.getFullYear()) return monthIndex > now.getMonth();
    return false;
  }

  isYearDisabled(year: number): boolean {
    return year > new Date().getFullYear();
  }

  private loadMonthView(): void {
    this.dayMap = {};
    this.monthViewResponse = null;
    this.monthViewItems = [];

    if (!this.memberId) {
      this.generateCalendar();
      return;
    }

    // API expects 1-based month
    const apiMonth = this.selectedMonth + 1;
    const apiYear = this.selectedYear;

    this.isLoading = true;
    this.dashboard.getReminderMonthView(this.memberId, apiMonth, apiYear).subscribe({
      next: (res) => {
        this.monthViewResponse = res;
        this.processMonthView(res);
        this.generateCalendar();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching habits month view:', err);
        this.dayMap = {};
        this.generateCalendar();
        this.isLoading = false;
      },
    });
  }

  private processMonthView(data: any): void {
    const normalized = data?.result ?? data;
    const items = Array.isArray(normalized) ? normalized : normalized ? [normalized] : [];
    this.monthViewItems = items;

    items.forEach((item: any) => {
      const dateKey = this.extractDateKey(item);
      if (!dateKey) return;
      if (!this.dayMap[dateKey]) this.dayMap[dateKey] = { entries: 0, completed: 0 };
      // API shape: { date: 'YYYY-MM-DD', reminders: [ { slots: [...] }, ... ] }
      // We want "Entries" = total slots count across all reminders for that date.
      const reminders = Array.isArray(item?.reminders) ? item.reminders : [];
      const slotCount = reminders.reduce((acc: number, r: any) => {
        const slots = Array.isArray(r?.slots) ? r.slots : [];
        return acc + slots.length;
      }, 0);
      const completedCount = reminders.reduce((acc: number, r: any) => {
        const slots = Array.isArray(r?.slots) ? r.slots : [];
        return acc + slots.filter((s: any) => (s?.status ?? '').toString().toLowerCase() === 'done').length;
      }, 0);
      this.dayMap[dateKey].entries += slotCount;
      this.dayMap[dateKey].completed += completedCount;
    });
  }

  private extractDateKey(item: any): string | null {
    if (item?.dateKey && typeof item.dateKey === 'string') {
      const v = item.dateKey.trim();
      return v ? v : null;
    }
    if (item?.date) {
      // MonthView returns YYYY-MM-DD already; keep exact value to avoid timezone shifts.
      if (typeof item.date === 'string' && item.date.trim()) return item.date.trim();
      try {
        const d = new Date(item.date);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      } catch {
        return null;
      }
    }
    return null;
  }

  private generateCalendar(): void {
    this.calendarDays = [];
    const firstDay = new Date(this.selectedYear, this.selectedMonth, 1);
    const lastDay = new Date(this.selectedYear, this.selectedMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    for (let i = 0; i < startingDayOfWeek; i++) {
      this.calendarDays.push({ day: null, date: null, isCurrentMonth: false });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(this.selectedYear, this.selectedMonth, day);
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const dateKey = `${y}-${m}-${d}`;
      const summary = this.dayMap[dateKey] ?? { entries: 0, completed: 0 };

      this.calendarDays.push({
        day,
        date,
        dateKey,
        isCurrentMonth: true,
        entries: summary.entries ?? 0,
        completed: summary.completed ?? 0,
      });
    }

    const totalCells = this.calendarDays.length;
    const remainingCells = 7 - (totalCells % 7);
    if (remainingCells < 7) {
      for (let i = 0; i < remainingCells; i++) {
        this.calendarDays.push({ day: null, date: null, isCurrentMonth: false });
      }
    }
  }

  onDayClick(day: any): void {
    if (!day || !day.isCurrentMonth) return;
    const entries = Number(day.entries ?? 0);
    if (!entries || entries <= 0) return;
    const dateKey = (day.dateKey ?? '') as string;
    if (!dateKey) return;

    const apiDay = this.monthViewItems.find((x: any) => (this.extractDateKey(x) ?? '') === dateKey);
    const reminders = Array.isArray(apiDay?.reminders) ? apiDay.reminders : [];

    const cards: HabitSlotCard[] = reminders.flatMap((r: any) => {
      const slots = Array.isArray(r?.slots) ? r.slots : [];
      return slots.map((s: any) => ({
        habitIcon: (r?.habitIcon ?? null) as string | null,
        habitName: (r?.habitName ?? null) as string | null,
        title: (r?.title ?? null) as string | null,
        description: (r?.description ?? null) as string | null,
        habitJourneyDescription: (r?.habitJourneyDescription ?? null) as string | null,
        status: (s?.status ?? null) as string | null,
        time: (s?.time ?? null) as string | null,
      }));
    });

    this.sortHabitSlotCardsByTime(cards);

    // Title: DayName, Date
    const dateObj = new Date(`${dateKey}T00:00:00`);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[dateObj.getDay()];
    const formattedDate = dateKey; // keep YYYY-MM-DD as requested

    this.dialog.open(HabitsDetailDialogComponent, {
      width: '92%',
      maxWidth: '1350px',
      hasBackdrop: true,
      backdropClass: 'habits-dialog-backdrop',
      disableClose: false,
      data: {
        dayName,
        date: formattedDate,
        cards,
      },
    });
  }

  /** Earliest time first (24h strings from API, e.g. 11:00, 12:00, 12:30). Unparseable / missing times sort last. */
  private sortHabitSlotCardsByTime(cards: HabitSlotCard[]): void {
    const toMinutes = (time: string | null): number | null => {
      if (!time) return null;
      const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(time.trim());
      if (!m) return null;
      const hh = Number(m[1]);
      const mm = Number(m[2]);
      const ss = m[3] != null ? Number(m[3]) : 0;
      if (!Number.isFinite(hh) || !Number.isFinite(mm) || !Number.isFinite(ss)) return null;
      return hh * 60 + mm + ss / 60;
    };

    cards.sort((a, b) => {
      const ma = toMinutes(a.time);
      const mb = toMinutes(b.time);
      if (ma === null && mb === null) return 0;
      if (ma === null) return 1;
      if (mb === null) return -1;
      if (ma !== mb) return ma - mb;
      const ta = `${a.habitName ?? ''}\0${a.title ?? ''}`;
      const tb = `${b.habitName ?? ''}\0${b.title ?? ''}`;
      return ta.localeCompare(tb);
    });
  }
}

@Component({
  selector: 'app-habits-detail-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './habits-detail-dialog.component.html',
  styleUrl: './habits-detail-dialog.component.css',
})
export class HabitsDetailDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<HabitsDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { dayName: string; date: string; cards: HabitSlotCard[] }
  ) {}

  closeDialog(): void {
    this.dialogRef.close();
  }

  formatTime24To12(time24: string | null): string {
    if (!time24) return '';
    const m = /^(\d{1,2}):(\d{2})$/.exec(time24.trim());
    if (!m) return time24;
    let hh = Number(m[1]);
    const mm = m[2];
    if (!Number.isFinite(hh)) return time24;
    const ampm = hh >= 12 ? 'PM' : 'AM';
    hh = hh % 12;
    if (hh === 0) hh = 12;
    return `${hh}:${mm} ${ampm}`;
  }

  resolveTitle(card: HabitSlotCard): string {
    const text = card.habitJourneyDescription || card.title || '';
    return text.replace(/\[time\]/gi, this.formatTime24To12(card.time));
  }
}

